import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

/**
 * Filter shared by every PartnerDashboard call. Build a `params` object from the
 * active filter, omitting empty keys (see `params()` below).
 *
 * It is partner-scoped end-to-end: a partner user (role `User`) never sends a
 * `partnerId` — the backend derives the partner from the JWT. Only an admin sets
 * `partnerId` to drill into one partner.
 */
export interface DashboardFilter {
  partnerId?: number; // admin only
  year?: number; // calendar year, e.g. 2026
  month?: number; // 1–12 (only with year)
  finYear?: number; // financial year START: 2025 => FY 2025-26 (Apr–Mar)
  fromDate?: string; // 'yyyy-MM-dd' (overrides year/finYear)
  toDate?: string; // 'yyyy-MM-dd' inclusive
  search?: string;
  paymentStatus?: string;
  kycStatus?: string;
  status?: string;
  page?: number; // default 1
  pageSize?: number; // default 20 (max 200)
}

/** The current Indian financial-year START year (FY runs Apr–Mar). e.g. June 2026 => 2026. */
export function currentFinYear(d: Date = new Date()): number {
  const y = d.getFullYear();
  return d.getMonth() + 1 >= 4 ? y : y - 1;
}

// ===================== response models =====================

/** Echoed by Overview: who this view is scoped to. */
export interface DashboardScope {
  partnerId: number | null;
  partnerName: string | null;
  isAdminView: boolean;
}

/** Echoed by Overview: the resolved date window the backend actually queried. */
export interface DashboardPeriod {
  fromDate: string | null;
  toDate: string | null;
  year: number | null;
  month: number | null;
  finYear: number | null;
}

export interface DashboardSummary {
  totalMembers: number;
  paidMembers: number;
  unpaidMembers: number;
  refundedMembers: number;
  totalPremium: number;
  collectedAmount: number;
  pendingAmount: number;
  kycVerified: number;
  kycPending: number;
}

export interface PaymentStats {
  totalTransactions: number;
  successCount: number;
  successAmount: number;
  failedCount: number;
  failedAmount: number;
  pendingCount: number;
  pendingAmount: number;
}

export interface MonthlyTrendPoint {
  yr: number;
  mon: number;
  members: number;
  collected: number;
  pending: number;
  label: string;
}

/** A row in `recentMembers` and in the Insured Members table (email only on the table). */
export interface MemberRow {
  id: number;
  memberUniqueId: string | null;
  memberSeriesNo: string | null;
  fullName: string | null;
  mobileNumber: string | null;
  email?: string | null;
  masterPolicyName: string | null;
  /** Null until the policy is issued. PAID with a null here = paid but never issued. */
  policyNo?: string | null;
  grossAmt: number;
  paidAmount: number;
  paymentStatus: string | null;
  kycStatus: string | null;
  status: string | null;
  paidAt: string | null;
  createdAt: string | null;
}

/** A row in both SuccessfulPayments and FailedPayments (`failureReason` is the useful extra). */
export interface PaymentRow {
  id: number;
  memberId: number;
  memberUniqueId: string | null;
  memberName: string | null;
  memberSeriesNo: string | null;
  paymentUniqueId: string | null;
  amount: number;
  currency: string | null;
  paymentMode: string | null;
  paymentStatus: string | null;
  payerType: string | null;
  gatewayName: string | null;
  gatewayOrderId: string | null;
  gatewayPaymentId: string | null;
  failureReason: string | null;
  paidAt: string | null;
  createdDate: string | null;
}

export interface DashboardOverview {
  scope: DashboardScope;
  period: DashboardPeriod;
  summary: DashboardSummary;
  paymentStats: PaymentStats;
  monthlyTrend: MonthlyTrendPoint[];
  recentMembers: MemberRow[];
}

/** Standard paged envelope `data` for the three tables. */
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ===================== member lookup =====================

export interface MemberLookupQuery {
  query?: string; // single-box value (id / loan / uniqueId / seriesNo / mobile)
  memberId?: number;
  loanNumber?: string;
  memberUniqueId?: string;
  mobileNumber?: string;
  partnerId?: number; // admin only
}

export interface MemberLookupMember {
  id: number;
  memberUniqueId: string | null;
  memberSeriesNo: string | null;
  masterPolicyName: string | null;
  masterPolicyOnLoan: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  loanNumber: string | null;
  dateOfBirth: string | null;
  age: number;
  gender: string | null;
  panNumber: string | null;
  aadhaarNumber: string | null;
  email: string | null;
  mobileNumber: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  policyStartDate: string | null;
  policyEndDate: string | null;
  doctorConsultantAMT: number;
  basePrimume: number;
  gstPer: number;
  gstAmt: number;
  premiumWithGST: number;
  grossAmt: number;
  kycStatus: string | null;
  status: string | null;
  paymentStatus: string | null;
  paidAmount: number;
  paidAt: string | null;
  createdAt: string | null;
}

export interface MemberNominee {
  id: number;
  memberId: number;
  nomineeName: string | null;
  relationship: string | null;
  dateOfBirth: string | null;
  nominationPercentage: number;
  mobileNumber: string | null;
  email: string | null;
}

export interface LookupPaymentSummary {
  premiumDue: number;
  totalPaid: number;
  balance: number;
  paymentStatus: string | null;
  totalTransactions: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
  lastPaidAt: string | null;
}

export interface MemberLookupResult {
  member: MemberLookupMember;
  nominees: MemberNominee[];
  payments: PaymentRow[];
  paymentSummary: LookupPaymentSummary;
}

// ===================== PascalCase → camelCase normalization =====================
// Responses here are already camelCase, but keep the defensive mapper for consistency
// with the rest of the app (see services/payment.ts).

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asStr(v: any, dflt: string | null = null): string | null {
  return v == null ? dflt : String(v);
}

function mapScope(x: any): DashboardScope {
  return {
    partnerId: pick<number>(x, 'PartnerId', 'partnerId') ?? null,
    partnerName: asStr(pick(x, 'PartnerName', 'partnerName')),
    isAdminView: asBool(pick(x, 'IsAdminView', 'isAdminView')),
  };
}

function mapPeriod(x: any): DashboardPeriod {
  return {
    fromDate: asStr(pick(x, 'FromDate', 'fromDate')),
    toDate: asStr(pick(x, 'ToDate', 'toDate')),
    year: pick<number>(x, 'Year', 'year') ?? null,
    month: pick<number>(x, 'Month', 'month') ?? null,
    finYear: pick<number>(x, 'FinYear', 'finYear') ?? null,
  };
}

function mapSummary(x: any): DashboardSummary {
  return {
    totalMembers: asNum(pick(x, 'TotalMembers', 'totalMembers')),
    paidMembers: asNum(pick(x, 'PaidMembers', 'paidMembers')),
    unpaidMembers: asNum(pick(x, 'UnpaidMembers', 'unpaidMembers')),
    refundedMembers: asNum(pick(x, 'RefundedMembers', 'refundedMembers')),
    totalPremium: asNum(pick(x, 'TotalPremium', 'totalPremium')),
    collectedAmount: asNum(pick(x, 'CollectedAmount', 'collectedAmount')),
    pendingAmount: asNum(pick(x, 'PendingAmount', 'pendingAmount')),
    kycVerified: asNum(pick(x, 'KycVerified', 'kycVerified')),
    kycPending: asNum(pick(x, 'KycPending', 'kycPending')),
  };
}

function mapStats(x: any): PaymentStats {
  return {
    totalTransactions: asNum(pick(x, 'TotalTransactions', 'totalTransactions')),
    successCount: asNum(pick(x, 'SuccessCount', 'successCount')),
    successAmount: asNum(pick(x, 'SuccessAmount', 'successAmount')),
    failedCount: asNum(pick(x, 'FailedCount', 'failedCount')),
    failedAmount: asNum(pick(x, 'FailedAmount', 'failedAmount')),
    pendingCount: asNum(pick(x, 'PendingCount', 'pendingCount')),
    pendingAmount: asNum(pick(x, 'PendingAmount', 'pendingAmount')),
  };
}

function mapTrend(x: any): MonthlyTrendPoint {
  return {
    yr: asNum(pick(x, 'Yr', 'yr', 'Year', 'year')),
    mon: asNum(pick(x, 'Mon', 'mon', 'Month', 'month')),
    members: asNum(pick(x, 'Members', 'members')),
    collected: asNum(pick(x, 'Collected', 'collected')),
    pending: asNum(pick(x, 'Pending', 'pending')),
    label: asStr(pick(x, 'Label', 'label')) ?? '',
  };
}

function mapMember(x: any): MemberRow {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    memberUniqueId: asStr(pick(x, 'MemberUniqueId', 'memberUniqueId')),
    memberSeriesNo: asStr(pick(x, 'MemberSeriesNo', 'memberSeriesNo')),
    fullName: asStr(pick(x, 'FullName', 'fullName')),
    mobileNumber: asStr(pick(x, 'MobileNumber', 'mobileNumber')),
    email: asStr(pick(x, 'Email', 'email')),
    masterPolicyName: asStr(pick(x, 'MasterPolicyName', 'masterPolicyName')),
    policyNo: asStr(pick(x, 'PolicyNo', 'policyNo')),
    grossAmt: asNum(pick(x, 'GrossAmt', 'grossAmt')),
    paidAmount: asNum(pick(x, 'PaidAmount', 'paidAmount')),
    paymentStatus: asStr(pick(x, 'PaymentStatus', 'paymentStatus')),
    kycStatus: asStr(pick(x, 'KycStatus', 'kycStatus')),
    status: asStr(pick(x, 'Status', 'status')),
    paidAt: asStr(pick(x, 'PaidAt', 'paidAt')),
    createdAt: asStr(pick(x, 'CreatedAt', 'createdAt')),
  };
}

function mapPayment(x: any): PaymentRow {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    memberId: asNum(pick(x, 'MemberId', 'memberId')),
    memberUniqueId: asStr(pick(x, 'MemberUniqueId', 'memberUniqueId')),
    memberName: asStr(pick(x, 'MemberName', 'memberName')),
    memberSeriesNo: asStr(pick(x, 'MemberSeriesNo', 'memberSeriesNo')),
    paymentUniqueId: asStr(pick(x, 'PaymentUniqueId', 'paymentUniqueId')),
    amount: asNum(pick(x, 'Amount', 'amount')),
    currency: asStr(pick(x, 'Currency', 'currency')) ?? 'INR',
    paymentMode: asStr(pick(x, 'PaymentMode', 'paymentMode')),
    paymentStatus: asStr(pick(x, 'PaymentStatus', 'paymentStatus')),
    payerType: asStr(pick(x, 'PayerType', 'payerType')),
    gatewayName: asStr(pick(x, 'GatewayName', 'gatewayName')),
    gatewayOrderId: asStr(pick(x, 'GatewayOrderId', 'gatewayOrderId')),
    gatewayPaymentId: asStr(pick(x, 'GatewayPaymentId', 'gatewayPaymentId')),
    failureReason: asStr(pick(x, 'FailureReason', 'failureReason')),
    paidAt: asStr(pick(x, 'PaidAt', 'paidAt')),
    createdDate: asStr(pick(x, 'CreatedDate', 'createdDate')),
  };
}

function mapOverview(x: any): DashboardOverview {
  const trend = pick<any[]>(x, 'MonthlyTrend', 'monthlyTrend');
  const recent = pick<any[]>(x, 'RecentMembers', 'recentMembers');
  return {
    scope: mapScope(pick(x, 'Scope', 'scope') ?? {}),
    period: mapPeriod(pick(x, 'Period', 'period') ?? {}),
    summary: mapSummary(pick(x, 'Summary', 'summary') ?? {}),
    paymentStats: mapStats(pick(x, 'PaymentStats', 'paymentStats') ?? {}),
    monthlyTrend: Array.isArray(trend) ? trend.map(mapTrend) : [],
    recentMembers: Array.isArray(recent) ? recent.map(mapMember) : [],
  };
}

/** Map an envelope whose `data` is a single object. */
function mapData<T>(fn: (x: any) => T) {
  return (res: any): ApiEnvelope<T> => (res?.data ? { ...res, data: fn(res.data) } : res);
}

/** Map an envelope whose `data` is a PagedResult, applying `fn` to each item. */
function mapPaged<T>(fn: (x: any) => T) {
  return (res: any): ApiEnvelope<PagedResult<T>> => {
    const d = res?.data;
    if (!d) return res;
    const rawItems = pick<any[]>(d, 'Items', 'items');
    const items = Array.isArray(rawItems) ? rawItems.map(fn) : [];
    return {
      ...res,
      data: {
        items,
        totalCount: asNum(pick(d, 'TotalCount', 'totalCount')),
        page: asNum(pick(d, 'Page', 'page'), 1),
        pageSize: asNum(pick(d, 'PageSize', 'pageSize'), items.length),
        totalPages: asNum(pick(d, 'TotalPages', 'totalPages')),
      },
    };
  };
}

function mapLookupMember(x: any): MemberLookupMember {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    memberUniqueId: asStr(pick(x, 'MemberUniqueId', 'memberUniqueId')),
    memberSeriesNo: asStr(pick(x, 'MemberSeriesNo', 'memberSeriesNo')),
    masterPolicyName: asStr(pick(x, 'MasterPolicyName', 'masterPolicyName')),
    masterPolicyOnLoan: asStr(
      pick(x, 'MasterPolicyONLOAN', 'masterPolicyONLOAN', 'MasterPolicyOnLoan', 'masterPolicyOnLoan'),
    ),
    firstName: asStr(pick(x, 'FirstName', 'firstName')),
    lastName: asStr(pick(x, 'LastName', 'lastName')),
    fullName: asStr(pick(x, 'FullName', 'fullName')),
    loanNumber: asStr(pick(x, 'LoanNumber', 'loanNumber')),
    dateOfBirth: asStr(pick(x, 'DateOfBirth', 'dateOfBirth')),
    age: asNum(pick(x, 'Age', 'age')),
    gender: asStr(pick(x, 'Gender', 'gender')),
    panNumber: asStr(pick(x, 'PanNumber', 'panNumber')),
    aadhaarNumber: asStr(pick(x, 'AadhaarNumber', 'aadhaarNumber')),
    email: asStr(pick(x, 'Email', 'email')),
    mobileNumber: asStr(pick(x, 'MobileNumber', 'mobileNumber')),
    address: asStr(pick(x, 'Address', 'address')),
    city: asStr(pick(x, 'City', 'city')),
    state: asStr(pick(x, 'State', 'state')),
    pincode: asStr(pick(x, 'Pincode', 'pincode')),
    policyStartDate: asStr(pick(x, 'PolicyStartDate', 'policyStartDate')),
    policyEndDate: asStr(pick(x, 'PolicyEndDate', 'policyEndDate')),
    doctorConsultantAMT: asNum(pick(x, 'DoctorConsultantAMT', 'doctorConsultantAMT')),
    basePrimume: asNum(pick(x, 'BasePrimume', 'basePrimume')),
    gstPer: asNum(pick(x, 'GstPer', 'gstPer')),
    gstAmt: asNum(pick(x, 'GstAmt', 'gstAmt')),
    premiumWithGST: asNum(pick(x, 'PremiumWithGST', 'premiumWithGST')),
    grossAmt: asNum(pick(x, 'GrossAmt', 'grossAmt')),
    kycStatus: asStr(pick(x, 'KycStatus', 'kycStatus')),
    status: asStr(pick(x, 'Status', 'status')),
    paymentStatus: asStr(pick(x, 'PaymentStatus', 'paymentStatus')),
    paidAmount: asNum(pick(x, 'PaidAmount', 'paidAmount')),
    paidAt: asStr(pick(x, 'PaidAt', 'paidAt')),
    createdAt: asStr(pick(x, 'CreatedAt', 'createdAt')),
  };
}

function mapNominee(x: any): MemberNominee {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    memberId: asNum(pick(x, 'MemberId', 'memberId')),
    nomineeName: asStr(pick(x, 'NomineeName', 'nomineeName')),
    relationship: asStr(pick(x, 'Relationship', 'relationship')),
    dateOfBirth: asStr(pick(x, 'DateOfBirth', 'dateOfBirth')),
    nominationPercentage: asNum(pick(x, 'NominationPercentage', 'nominationPercentage')),
    mobileNumber: asStr(pick(x, 'MobileNumber', 'mobileNumber')),
    email: asStr(pick(x, 'Email', 'email')),
  };
}

function mapLookupSummary(x: any): LookupPaymentSummary {
  return {
    premiumDue: asNum(pick(x, 'PremiumDue', 'premiumDue')),
    totalPaid: asNum(pick(x, 'TotalPaid', 'totalPaid')),
    balance: asNum(pick(x, 'Balance', 'balance')),
    paymentStatus: asStr(pick(x, 'PaymentStatus', 'paymentStatus')),
    totalTransactions: asNum(pick(x, 'TotalTransactions', 'totalTransactions')),
    successCount: asNum(pick(x, 'SuccessCount', 'successCount')),
    failedCount: asNum(pick(x, 'FailedCount', 'failedCount')),
    pendingCount: asNum(pick(x, 'PendingCount', 'pendingCount')),
    lastPaidAt: asStr(pick(x, 'LastPaidAt', 'lastPaidAt')),
  };
}

function mapLookupResult(x: any): MemberLookupResult {
  const noms = pick<any[]>(x, 'Nominees', 'nominees');
  const pays = pick<any[]>(x, 'Payments', 'payments');
  return {
    member: mapLookupMember(pick(x, 'Member', 'member') ?? {}),
    nominees: Array.isArray(noms) ? noms.map(mapNominee) : [],
    payments: Array.isArray(pays) ? pays.map(mapPayment) : [],
    paymentSummary: mapLookupSummary(pick(x, 'PaymentSummary', 'paymentSummary') ?? {}),
  };
}

@Injectable({ providedIn: 'root' })
export class PartnerDashboardService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  /** Build an HttpParams-friendly object from the filter, omitting null/undefined/'' keys. */
  private params(f: DashboardFilter) {
    const p: any = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') p[k] = v;
    });
    return p;
  }

  overview(f: DashboardFilter) {
    return this.http
      .get<ApiEnvelope<DashboardOverview>>(`${this.api}/PartnerDashboard/Overview`, {
        params: this.params(f),
      })
      .pipe(map(mapData(mapOverview)));
  }

  insuredMembers(f: DashboardFilter) {
    return this.http
      .get<ApiEnvelope<PagedResult<MemberRow>>>(`${this.api}/PartnerDashboard/InsuredMembers`, {
        params: this.params(f),
      })
      .pipe(map(mapPaged(mapMember)));
  }

  successfulPayments(f: DashboardFilter) {
    return this.http
      .get<ApiEnvelope<PagedResult<PaymentRow>>>(`${this.api}/PartnerDashboard/SuccessfulPayments`, {
        params: this.params(f),
      })
      .pipe(map(mapPaged(mapPayment)));
  }

  failedPayments(f: DashboardFilter) {
    return this.http
      .get<ApiEnvelope<PagedResult<PaymentRow>>>(`${this.api}/PartnerDashboard/FailedPayments`, {
        params: this.params(f),
      })
      .pipe(map(mapPaged(mapPayment)));
  }

  /** Precise member lookup (id / loan / unique id / series no / mobile). Returns an array of matches. */
  memberLookup(q: MemberLookupQuery) {
    return this.http
      .get<ApiEnvelope<MemberLookupResult[]>>(`${this.api}/PartnerDashboard/MemberLookup`, {
        params: this.params(q as any),
      })
      .pipe(
        map((res: any) =>
          res && Array.isArray(res.data) ? { ...res, data: res.data.map(mapLookupResult) } : res,
        ),
      );
  }
}
